import { useRef, useState } from "react";
import { api, BACKEND_URL, tokenStore } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./ui/dialog";
import { Upload, FileText, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function fileUrl(fileId) {
  const token = tokenStore.get();
  return `${BACKEND_URL}/api/files/${fileId}?auth=${encodeURIComponent(token)}`;
}

export function DocumentRow({ doc, onDelete, canDelete }) {
  return (
    <div
      data-testid={`doc-row-${doc.id}`}
      className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-white hover:border-[#BD984D]/40"
    >
      <div className="p-2 bg-[#BD984D]/10 rounded-md text-[#A57F39]">
        <FileText className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[#0C1824] truncate">{doc.title}</div>
        {doc.description && (
          <div className="text-xs text-[#2B3F4D] mt-0.5">{doc.description}</div>
        )}
        <div className="text-[11px] text-slate-500 mt-1">
          {doc.original_filename}
          {doc.created_by_name && <> · lastet opp av {doc.created_by_name}</>}
          {doc.created_at && <> · {new Date(doc.created_at).toLocaleDateString("nb-NO")}</>}
        </div>
      </div>
      <div className="flex gap-1">
        <a href={fileUrl(doc.file_id)} target="_blank" rel="noreferrer"
           data-testid={`doc-open-${doc.id}`}>
          <Button variant="outline" size="sm">
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Åpne
          </Button>
        </a>
        {canDelete && (
          <Button variant="outline" size="sm" onClick={() => onDelete(doc.id)}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`doc-delete-${doc.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function DocumentUploader({ scope, bookingId, onUploaded, openTestId = "doc-upload-open" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => { setForm({ title: "", description: "" }); setFile(null); };

  const submit = async () => {
    if (!file) return toast.error("Velg en PDF-fil");
    if (!form.title.trim()) return toast.error("Skriv inn tittel");
    setBusy(true);
    try {
      // 1) Upload the file
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // 2) Create the document metadata
      await api.post("/documents", {
        title: form.title.trim(),
        description: form.description.trim(),
        file_id: up.data.id,
        scope,
        booking_id: scope === "booking" ? bookingId : null,
      });
      toast.success("Dokument lastet opp");
      setOpen(false);
      reset();
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opplasting feilet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid={openTestId} className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
          <Upload className="w-4 h-4 mr-1.5" /> Last opp dokument
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nytt dokument</DialogTitle>
          <DialogDescription>
            {scope === "global"
              ? "Dette dokumentet vises automatisk hos alle kunder (eksisterende og nye)."
              : "Dette dokumentet vises kun for kunden på denne bookingen."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tittel</Label>
            <Input value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="F.eks. Velkommen ombord-brosjyre"
                   data-testid="doc-upload-title" />
          </div>
          <div>
            <Label>Beskrivelse (valgfri)</Label>
            <Textarea value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={2}
                      data-testid="doc-upload-desc" />
          </div>
          <div>
            <Label>PDF-fil</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              data-testid="doc-upload-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-[#2B3F4D] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[#BD984D]/10 file:text-[#A57F39] hover:file:bg-[#BD984D]/20"
            />
            {file && (
              <div className="text-xs text-[#2B3F4D] mt-1.5">
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="doc-upload-save"
                  className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
            {busy ? "Laster opp…" : "Last opp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
